// Draws one frame of the world: the floor image, the people in the scene in
// their layers (bodies, then the furniture that covers them, then forearms),
// the dimming outside the lit room, and the reduced-motion ink veil.
// Pure drawing: all state comes in as arguments.
import { atlas, image, meta, type Atlas, type FloorId, type FxEntry, type WorldMeta } from './assets';
import { DIM_ALPHA, viewOrigin, type CamState, type Viewport } from './camera';
import type { Actor, BenchActor, ConfActor, Face, Scene, StandActor } from './scene';
import { castActors } from './life';
import { drawClouds, drawFx, drawLamp, fxLayer, fxSortY, type FxContext } from './fx';
import type { CastOverride } from './life';

// Every room's own effects (src/world/fx_<rooms>.ts register themselves), loaded here once so an effect draws whichever
// floor is seen first, mid-transition included.
import.meta.glob(['./fx_*.ts', '!./fx_*.test.ts'], { eager: true });

const INK = '#160f1a';

function drawFrame(ctx: CanvasRenderingContext2D, a: Atlas, name: string, x: number, y: number): boolean {
  const f = a.frames[name];
  if (!f || !a.img) return false;
  ctx.drawImage(a.img, f[0], f[1], f[2], f[3], x - f[4], y - f[5], f[2], f[3]);
  return true;
}

/** A stable small hash so each person blinks on their own rhythm. */
function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return (h >>> 0) % 10007;
}

/** The face actually drawn this frame: talking alternates the mouth, idle people blink. */
export function liveFace(actor: Actor, pose: string | null, now: number, idle: boolean): Face {
  let face: Face = actor.face ?? 'calm';
  if ((pose === 'write' || pose === 'read') && face === 'calm') face = 'down';
  if (actor.talking) {
    return Math.floor(now / 140 + hash(actor.person)) % 3 === 0 ? 'calm' : 'talk';
  }
  if (idle && face !== 'closed' && face !== 'down') {
    const period = 3000 + (hash(actor.person) % 4000);
    if ((now + hash(actor.person) * 7) % period < 170) return 'closed';
  }
  return face;
}

/** Where a person looks this frame: as told, or, for someone nobody has told, an occasional glance aside. */
export function liveLook(actor: Actor, now: number, idle: boolean): number {
  if (actor.look !== undefined) return actor.look;
  if (!idle || actor.talking) return 0;
  const h = hash(actor.person);
  const period = 7000 + (h % 6000);
  const t = (now + h * 13) % period;
  if (t >= 1300) return 0;
  return h % 2 === 0 ? 1 : -1;
}

function drawFace(ctx: CanvasRenderingContext2D, a: Atlas, set: string, face: Face, look: number, x: number, y: number) {
  const h = a.head[set];
  if (!h) return;
  drawFrame(ctx, a, `face.${face}.${look}`, x + h[0], y + h[1]);
}

function shadow(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.fillStyle = 'rgba(22,15,26,0.28)';
  ctx.fillRect(x - 8, y - 1, 18, 2);
  ctx.fillRect(x - 6, y + 1, 14, 1);
}

/** Draw a frame in two parts so the upper body can settle `bob` pixels (breathing) while the feet stay put. */
function drawFrameBob(ctx: CanvasRenderingContext2D, a: Atlas, name: string, x: number, y: number, split: number, bob: number): boolean {
  const f = a.frames[name];
  if (!f || !a.img) return false;
  if (!bob) return drawFrame(ctx, a, name, x, y);
  const sr = Math.max(0, Math.min(f[3], f[5] - split));
  if (f[3] - sr > 0) ctx.drawImage(a.img, f[0], f[1] + sr, f[2], f[3] - sr, x - f[4], y - f[5] + sr, f[2], f[3] - sr);
  if (sr > 0) ctx.drawImage(a.img, f[0], f[1], f[2], sr, x - f[4], y - f[5] + bob, f[2], sr);
  return true;
}

function clipAbove(ctx: CanvasRenderingContext2D, x: number, row: number): void {
  ctx.save();
  ctx.beginPath();
  ctx.rect(x - 80, row - 200, 160, 200);
  ctx.clip();
}

/** A standing or walking person in any view: profile walk cycle, breathing, a counter in front of them. */
function drawStand(ctx: CanvasRenderingContext2D, a: Atlas, act: StandActor, x: number, y: number, now: number, idle: boolean): void {
  if (act.clip !== undefined) clipAbove(ctx, x, act.clip);
  else if (!act.behind) shadow(ctx, x, y);
  const bob = act.bob ?? 0;
  const side = act.view === 'l' || act.view === 'r';
  let name: string;
  if (side) {
    name = act.step !== undefined ? `walk.${act.view}.${act.step}` : `stand.${act.view}`;
  } else {
    const moving = act.step !== undefined ? act.step % 2 === 1
      : act.walk || (act.tween ? now < act.tween.t0 + act.tween.dur && now >= act.tween.t0 : false);
    const step = act.step !== undefined ? moving : moving && Math.floor(now / 160) % 2 === 1;
    name = `${step ? 'walk' : 'stand'}.${act.view}`;
  }
  // an extra's atlas has no profile frames before M10's rebuild: fall back to facing us
  if (!a.frames[name]) name = side ? 'stand.front' : name;
  if (act.arms) {
    const armed = act.arms === 'hold' ? `${name}.hold` : name === 'stand.front' ? `stand.front.${act.arms}` : '';
    if (armed && a.frames[armed]) name = armed;
  }
  drawFrameBob(ctx, a, name, x, y, 15, bob);
  if (act.view === 'front' || (side && !a.frames[`stand.${act.view}`])) {
    drawFace(ctx, a, 'stand.front', liveFace(act, null, now, idle), liveLook(act, now, idle), x, y + bob);
  }
  if (act.clip !== undefined) ctx.restore();
}

/** The cast's seated and standing people, sorted by their feet so nearer people overlap farther ones. */
function drawLiving(ctx: CanvasRenderingContext2D, actors: Actor[], tall: FxEntry[], fxc: FxContext, now: number, idle: boolean): void {
  type Item = { y: number; draw: () => void };
  const items: Item[] = [];
  for (const act of actors) {
    const a = atlas(act.person);
    if (!a) continue;
    switch (act.kind) {
      case 'seat': {
        const bob = act.bob ?? 0;
        probePerson(a, `desk.${act.pose}.A`, act.x, act.y, act.person);
        items.push({ y: act.y, draw: () => {
          clipAbove(ctx, act.x, act.y);
          drawFrame(ctx, a, `desk.${act.pose}.A`, act.x, act.y + bob);
          drawFace(ctx, a, `desk.${act.pose}`, liveFace(act, act.pose, now, idle), liveLook(act, now, idle), act.x, act.y + bob);
          drawFrame(ctx, a, `desk.${act.pose}.B`, act.x, act.y + bob);
          ctx.restore();
          drawFrame(ctx, a, `desk.${act.pose}.C`, act.x + (act.armDx ?? 0), act.y);
        } });
        break;
      }
      case 'back':
        probePerson(a, 'back', act.x, act.y, act.person);
        items.push({ y: act.y, draw: () => { drawFrameBob(ctx, a, 'back', act.x, act.y, 6, act.bob ?? 0); } });
        break;
      case 'side': {
        const talk = act.talking && Math.floor(now / 140) % 3 !== 0 ? '.talk' : '';
        probePerson(a, `side.${act.facing}`, act.x, act.y, act.person);
        items.push({ y: act.y, draw: () => { drawFrameBob(ctx, a, `side.${act.facing}${talk}`, act.x, act.y, 6, act.bob ?? 0); } });
        break;
      }
      case 'stand':
        probePerson(a, `stand.${act.view}`, act.x, act.y, act.person);
        items.push({ y: act.y, draw: () => drawStand(ctx, a, act, act.x, act.y, now, idle) });
        break;
      default:
        break;
    }
  }
  for (const e of tall) items.push({ y: fxSortY(e, fxc), draw: () => drawFx(ctx, e, fxc) });
  items.sort((p, q) => p.y - q.y);
  for (const it of items) it.draw();
}

interface Placed { actor: Actor; a: Atlas; x: number; y: number }

/** Test hook: with window.__worldProbe set, each frame publishes the screen boxes (CSS px) of the people it drew as
 *  window.__worldPeople, so the layout probe can tell when the UI half-covers someone. Off in normal use. */
const probeBoxes: Array<[number, number, number, number, string]> = [];
let probing = false;
function probePerson(a: Atlas, frame: string, x: number, y: number, who: string): void {
  if (!probing) return;
  const f = a.frames[frame];
  if (f) probeBoxes.push([x - f[4], y - f[5], f[2], f[3], who]);
}

function drawPeople(ctx: CanvasRenderingContext2D, m: WorldMeta, actors: Actor[], now: number, idle: boolean, floor: FloorId) {
  const place = (actor: Actor): Placed | null => {
    const a = atlas(actor.person);
    if (!a) return null;
    switch (actor.kind) {
      case 'bench': { const s = m.seats[actor.seat]; return s ? { actor, a, x: s[0], y: s[1] } : null; }
      case 'conf': { const s = m.conf[actor.seat]; return s ? { actor, a, x: s.x, y: s.top } : null; }
      case 'desk': return { actor, a, x: m.desk[0], y: m.desk[1] };
      case 'counsel': { const s = m.counsel[actor.slot]; return s ? { actor, a, x: s[0], y: s[1] } : null; }
      case 'stand': {
        const tw = actor.tween;
        if (!tw) return { actor, a, x: actor.x, y: actor.y };
        if (now < tw.t0) return null;          // not yet through the drapes
        const k = Math.max(0, Math.min(1, (now - tw.t0) / tw.dur));
        return { actor, a, x: Math.round(tw.fromX + (actor.x - tw.fromX) * k), y: Math.round(tw.fromY + (actor.y - tw.fromY) * k) };
      }
      default: return null;
    }
  };
  const placed = actors.map(place).filter((p): p is Placed => p !== null);
  for (const p of placed) {
    const k = p.actor.kind;
    const frame = k === 'bench' ? `bench.${(p.actor as BenchActor).pose}.A` : k === 'conf' || k === 'desk' ? 'desk.folded.A'
      : k === 'counsel' ? 'back' : k === 'stand' ? `stand.${p.actor.view}` : '';
    if (frame) probePerson(p.a, frame, p.x, p.y, p.actor.person);
  }
  const bench = placed.filter((p) => p.actor.kind === 'bench').sort((p, q) => p.y - q.y);
  const confFar = placed.filter((p) => p.actor.kind === 'conf' && m.conf[(p.actor as ConfActor).seat]?.kind === 'far');
  const desk = placed.filter((p) => p.actor.kind === 'desk');
  const look = (p: Placed) => liveLook(p.actor, now, idle);

  // seated fronts: body and raised hands, then the face patch, then the furniture, then forearms
  const seatedFront = (list: Placed[], set: 'bench' | 'desk', poseOf: (p: Placed) => string) => {
    for (const p of list) {
      const pose = poseOf(p);
      drawFrame(ctx, p.a, `${set}.${pose}.A`, p.x, p.y);
      drawFace(ctx, p.a, `${set}.${pose}`, liveFace(p.actor, pose, now, idle), look(p), p.x, p.y);
      drawFrame(ctx, p.a, `${set}.${pose}.B`, p.x, p.y);
    }
  };
  const forearms = (list: Placed[], set: 'bench' | 'desk', poseOf: (p: Placed) => string) => {
    for (const p of list) drawFrame(ctx, p.a, `${set}.${poseOf(p)}.C`, p.x, p.y);
  };
  const benchPose = (p: Placed) => (p.actor as BenchActor).pose;
  const deskPose = (p: Placed) => (p.actor.kind === 'desk' ? p.actor.pose : 'folded');

  const drawStanding = (list: Placed[]) => {
    for (const p of list) {
      if (p.actor.kind === 'stand') drawStand(ctx, p.a, p.actor, p.x, p.y, now, idle);
    }
  };
  const standing = placed.filter((p) => p.actor.kind === 'stand').sort((p, q) => p.y - q.y);
  const behindBench = standing.filter((p) => p.actor.kind === 'stand' && p.actor.behind);

  if (floor === 'f1') {
    drawStanding(behindBench);
    seatedFront(bench, 'bench', benchPose);
    seatedFront(confFar, 'desk', deskPose);
    seatedFront(desk, 'desk', deskPose);
    for (const key of ['bench', 'conf', 'desk'] as const) {
      const ov = m.overlays[key];
      const im = image(ov.img);
      if (im) ctx.drawImage(im, ov.at[0], ov.at[1]);
    }
    forearms(bench, 'bench', benchPose);
    forearms(confFar, 'desk', deskPose);
    forearms(desk, 'desk', deskPose);
  }
  // conference cards, then the near side and the ends
  const props = image(m.props.img);
  for (const p of placed) {
    if (p.actor.kind !== 'conf') continue;
    const seat = m.conf[p.actor.seat];
    const v = p.actor.vote;
    if (props && v) {
      const f = m.props.frames[`card.${v}`];
      if (f) ctx.drawImage(props, f[0], f[1], f[2], f[3], seat.card[0], seat.card[1], f[2], f[3]);
    }
  }
  for (const p of placed) {
    const act = p.actor;
    if (act.kind === 'conf') {
      const kind = m.conf[act.seat]?.kind;
      const talk = act.talking && Math.floor(now / 140) % 3 !== 0 ? '.talk' : '';
      if (kind === 'near') drawFrame(ctx, p.a, 'back', p.x, p.y);
      else if (kind === 'end_l') drawFrame(ctx, p.a, `side.l${talk}`, p.x, p.y);
      else if (kind === 'end_r') drawFrame(ctx, p.a, `side.r${talk}`, p.x, p.y);
    } else if (act.kind === 'counsel') {
      drawFrame(ctx, p.a, 'back', p.x, p.y);
    }
  }
  // standing people last, nearest the viewer last
  drawStanding(standing.filter((p) => !(p.actor.kind === 'stand' && p.actor.behind)));
}

export interface DrawInput {
  cam: CamState;
  vp: Viewport;
  scene: Scene | null;
  now: number;
  /** Idle life (blinks) on; off under reduced motion. */
  idle: boolean;
  night: boolean;
  /** Wall-clock time for the clocks (Date.now() when absent; tests pin it). */
  clock?: number;
  /** Rooms whose route draws its own people there: their cast is left out. */
  skipRooms?: ReadonlySet<string>;
  /** Per cast id: change what one person is doing (a scene's officer talks while its notice is up), or null to hide them. */
  castOverride?: CastOverride;
}

export function drawWorld(ctx: CanvasRenderingContext2D, input: DrawInput): void {
  const { cam, vp, scene, now, idle } = input;
  const W = vp.w * vp.dpr;
  const H = vp.h * vp.dpr;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, W, H);
  const m = meta();
  if (!m) return;
  const [ox, oy] = viewOrigin(cam, vp);
  probing = typeof window !== 'undefined' && !!(window as unknown as { __worldProbe?: boolean }).__worldProbe;
  probeBoxes.length = 0;
  ctx.imageSmoothingEnabled = cam.scale < 1;
  ctx.setTransform(cam.scale, 0, 0, cam.scale, ox, oy);

  const drawFloor = (id: FloorId, alpha: number) => {
    const f = m.floors[id];
    const im = image(f.img);
    if (!im || alpha <= 0) return;
    ctx.globalAlpha = alpha;
    ctx.drawImage(im, f.at[0], f.at[1]);
    ctx.globalAlpha = 1;
  };
  drawFloor(cam.floor, 1);
  // what is in view, in floor pixels, with a margin for tall sprites
  const vx0 = -ox / cam.scale - 40;
  const vy0 = -oy / cam.scale - 60;
  const vx1 = (W - ox) / cam.scale + 40;
  const vy1 = (H - oy) / cam.scale + 60;
  const inView = (x: number, y: number) => x >= vx0 && x <= vx1 && y >= vy0 && y <= vy1;
  const fxc: FxContext = { m, now, date: new Date(input.clock ?? Date.now()), animate: idle, props: image(m.props.img) };
  const fxHere = (m.fx ?? []).filter((e) => e.floor === cam.floor && inView(e.x, e.y));
  for (const e of fxHere) if (fxLayer(e.kind) === 'ground') drawFx(ctx, e, fxc);
  if (scene?.actors) {
    const room = m.rooms[scene.room];
    if (room && room.floor === cam.floor) drawPeople(ctx, m, scene.actors, now, idle, cam.floor);
  }
  const cast = castActors(m, cam.floor, now, idle, input.skipRooms, input.castOverride).filter((a) => !('x' in a) || inView(a.x, a.y));
  drawLiving(ctx, cast, fxHere.filter((e) => fxLayer(e.kind) === 'tall'), fxc, now, idle);
  for (const e of fxHere) if (fxLayer(e.kind) === 'air') drawFx(ctx, e, fxc);
  if (cam.scale <= 1.5) drawClouds(ctx, m, now, idle);

  // dim everything outside the lit rect (walls included: grow it 6 px)
  if (cam.lit && cam.dim > 0) {
    const [x, y, w, h] = cam.lit;
    ctx.fillStyle = `rgba(22,15,26,${(DIM_ALPHA * cam.dim).toFixed(3)})`;
    ctx.beginPath();
    ctx.rect(-4000, -4000, 12000, 12000);
    ctx.rect(x - 6, y - 6, w + 12, h + 12);
    ctx.fill('evenodd');
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  if (scene?.hush) {
    ctx.globalCompositeOperation = 'saturation';
    ctx.fillStyle = 'rgba(128,128,128,0.45)';
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'source-over';
  }
  if (input.night) {
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = '#5a5f8c';
    ctx.fillRect(0, 0, W, H);
    // lamps light their surroundings after the dark, so the light is not darkened with everything else
    ctx.globalCompositeOperation = 'lighter';
    ctx.setTransform(cam.scale, 0, 0, cam.scale, ox, oy);
    for (const e of (m.fx ?? [])) if (e.kind === 'lamp' && e.floor === cam.floor && inView(e.x, e.y)) drawLamp(ctx, e, now, idle);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
  }
  if (probing) {
    const k = cam.scale / vp.dpr;
    (window as unknown as { __worldPeople: unknown }).__worldPeople = probeBoxes.map(([x, y, w, h, who]) => ({
      who, x: (ox / vp.dpr) + x * k, y: (oy / vp.dpr) + y * k, w: w * k, h: h * k,
    }));
  }
  if (cam.ink > 0) {
    ctx.fillStyle = INK;
    ctx.globalAlpha = cam.ink;
    ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = 1;
  }
}
